/*
 * Homepage featured-product gallery + buy box.
 *
 * Thumbnail strip drives the main stage. The offer module below is a port
 * of the PDP's inline quantity-card logic, so the homepage buy box computes
 * exactly the numbers the product page does. Add-to-cart itself is handled
 * by cart-drawer.js via the form's data-ajax-cart hook. If this script
 * never runs, the gallery shows the first image, the pre-selected bundle
 * is already highlighted server-side, and the form still posts the default
 * quantity — the cards simply stop switching.
 */
(function () {
  'use strict';

  function init(scope) {
    (scope || document).querySelectorAll('[data-fp-gallery]').forEach(function (gallery) {
      if (gallery.dataset.fpReady === '1') return;
      gallery.dataset.fpReady = '1';

      var main = gallery.querySelector('[data-fp-main]');
      var thumbs = [].slice.call(gallery.querySelectorAll('[data-fp-thumb]'));
      if (!main || thumbs.length < 2) return;

      function select(btn) {
        var src = btn.dataset.fpSrc;
        var srcset = btn.dataset.fpSrcset;
        if (!src) return;
        if (srcset) main.setAttribute('srcset', srcset);
        main.setAttribute('src', src);
        main.setAttribute('alt', btn.dataset.fpAlt || '');
        thumbs.forEach(function (t) {
          var on = t === btn;
          t.classList.toggle('is-current', on);
          t.setAttribute('aria-current', on ? 'true' : 'false');
        });
      }

      gallery.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-fp-thumb]');
        if (btn) select(btn);
      });

      // Arrow keys move through the strip, matching native listbox habits.
      gallery.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        var i = thumbs.indexOf(document.activeElement);
        if (i === -1) return;
        e.preventDefault();
        var next = thumbs[(i + (e.key === 'ArrowDown' ? 1 : thumbs.length - 1)) % thumbs.length];
        next.focus();
        select(next);
      });
    });
  }

  function initBuybox(scope) {
    (scope || document).querySelectorAll('[data-fp-buybox]').forEach(function (root) {
      if (root.dataset.fpBuyboxReady === '1') return;
      root.dataset.fpBuyboxReady = '1';

      var price = Number(root.dataset.price);
      var cards = [].slice.call(root.querySelectorAll('[data-qty-break]'));
      if (!price || !cards.length) return;

      var pcts = { 1: 0, 2: Number(root.dataset.pct2) || 0, 3: Number(root.dataset.pct3) || 0 };
      var threshold = Number(root.dataset.threshold) || 0;
      var bagLb = Number(root.dataset.bagLb) || 10;
      var bagName = root.dataset.bagName;
      var qtyInput = root.querySelector('[data-qty-input]');
      var atc = root.querySelector('[data-atc]');
      var summary = root.querySelector('[data-order-summary]');
      var bulkNote = root.querySelector('[data-bulk-note]');
      var qty = Number(root.dataset.defaultQty) || 1;
      // The largest tier actually offered, so the note follows the tiers
      // rather than assuming three of them.
      var topBreak = cards.reduce(function (m, c) {
        return Math.max(m, Number(c.dataset.qtyBreak) || 0);
      }, 1);

      function money(cents) {
        return '$' + (cents / 100).toFixed(2);
      }

      /*
       * Mirror how Shopify actually bills this, which is NOT a per-bag
       * percentage. The Admin rule is Buy-X-Get-Y: one bag is discounted and
       * the rest are full price, and Shopify FLOORS that single discount
       * (15% of $59.99 = $8.9985 -> $8.99). Modelling it as a percentage off
       * every bag and rounding put the 2-bag card a penny under what the cart
       * charged, and no percentage with one decimal could express 25%-off-one
       * across three bags at all.
       */
      function totals(n) {
        var full = price * n;
        var off = pcts[n] ? Math.floor(price * pcts[n] / 100) : 0;
        var discounted = full - off;
        return { full: full, discounted: discounted, saved: off };
      }

      function renderOffer() {
        var t = totals(qty);
        cards.forEach(function (c) {
          var n = Number(c.dataset.qtyBreak);
          var ct = totals(n);
          var perBag = c.querySelector('[data-card-perbag]');
          if (perBag) {
            perBag.textContent = money(Math.round(ct.discounted / n)) + '/bag';
          }
          var line = c.querySelector('[data-card-line]');
          if (line) {
            var text = money(ct.discounted) + ' total';
            if (threshold && ct.discounted >= threshold) text += ' · Free shipping';
            line.textContent = text;
          }
          var active = n === qty;
          c.classList.toggle('is-active', active);
          c.setAttribute('aria-checked', active ? 'true' : 'false');
        });

        if (qtyInput) qtyInput.value = qty;
        if (atc) {
          atc.textContent = 'Add ' + qty + (qty === 1 ? ' bag' : ' bags') + ' · ' + money(t.discounted);
        }
        // The note belongs to the largest bundle only — it answers "what if I
        // want more than this?", which is not a question the 1- or 2-bag card
        // raises. topBreak, not a literal 3, so it follows the tiers.
        if (bulkNote) bulkNote.hidden = qty !== topBreak;

        if (summary) {
          var rows = '<p class="pdp__order-head">Your order</p>' +
            '<p>' + qty + ' × ' + bagName + '</p>' +
            '<p>' + (qty * bagLb) + ' lb total</p>';
          if (t.saved > 0) rows += '<p class="pdp__order-save">You save ' + money(t.saved) + '</p>';
          if (threshold && t.discounted >= threshold) rows += '<p class="pdp__order-save">Free shipping</p>';
          summary.innerHTML = rows;
          summary.hidden = false;
        }
      }

      cards.forEach(function (c) {
        c.addEventListener('click', function () {
          var n = Number(c.dataset.qtyBreak);
          if (!pcts.hasOwnProperty(n)) return;
          qty = n;
          renderOffer();
        });
      });

      renderOffer();
    });
  }

  /*
   * Colour swatches: each tile is a link to the sibling colour's product
   * page. With the data the section embeds server-side we upgrade the click
   * in place — hero image, form variant and colour label switch to the
   * sibling. Sold-out siblings, a missing form or a price mismatch with the
   * hydrated buy box all fall back to plain navigation, as does no-JS.
   * Keyboard comes free: links are focusable and Enter fires click.
   */
  function initSwatches(scope) {
    (scope || document).querySelectorAll('[data-fp-buybox]').forEach(function (root) {
      if (root.dataset.fpSwatchReady === '1') return;
      root.dataset.fpSwatchReady = '1';

      var swatches = [].slice.call(root.querySelectorAll('[data-fp-swatch]'));
      if (!swatches.length) return;

      root.addEventListener('click', function (e) {
        var sw = e.target.closest('[data-fp-swatch]');
        if (!sw || !root.contains(sw)) return;

        var main = root.querySelector('[data-fp-main]');
        var idInput = root.querySelector('.pdp__form input[name="id"]');
        if (!sw.dataset.fpVariant || !main || !idInput) return;
        if (root.dataset.price && sw.dataset.fpPrice !== root.dataset.price) return;

        e.preventDefault();

        // The form now adds the sibling: cart-drawer.js reads FormData at
        // submit time, so the hidden id input is the whole contract.
        idInput.value = sw.dataset.fpVariant;

        // Hero slide becomes the sibling's featured image…
        main.setAttribute('srcset', sw.dataset.fpSrcset || '');
        main.setAttribute('src', sw.dataset.fpSrc);
        main.setAttribute('alt', sw.dataset.fpAlt || '');

        // …and the first thumbnail adopts it too, so re-selecting it after
        // browsing the other (unchanged) slides restores the sibling hero.
        var thumbs = [].slice.call(root.querySelectorAll('[data-fp-thumb]'));
        if (thumbs.length) {
          var first = thumbs[0];
          first.dataset.fpSrc = sw.dataset.fpSrc;
          first.dataset.fpSrcset = sw.dataset.fpSrcset || '';
          first.dataset.fpAlt = sw.dataset.fpAlt || '';
          // The swatch is a colour dot now, so the thumb preview comes from
          // the sibling's hero URL rather than from an image inside it.
          var tImg = first.querySelector('img');
          if (tImg) {
            tImg.src = sw.dataset.fpSrc;
            tImg.srcset = sw.dataset.fpSrcset || '';
          }
          thumbs.forEach(function (t, i) {
            t.classList.toggle('is-current', i === 0);
            t.setAttribute('aria-current', i === 0 ? 'true' : 'false');
          });
        }

        // Sold-out siblings swap in place too, with the buy button greyed
        // and labelled; swapping back to a sellable colour restores the
        // original label (which carries the computed price).
        var atcBtn = root.querySelector('[data-atc]');
        if (atcBtn) {
          if (sw.dataset.fpAvailable === '0') {
            if (!atcBtn.dataset.origLabel) atcBtn.dataset.origLabel = atcBtn.textContent;
            atcBtn.disabled = true;
            atcBtn.classList.add('pdp__atc--soldout');
            atcBtn.textContent = 'Out of stock';
          } else if (atcBtn.disabled) {
            atcBtn.disabled = false;
            atcBtn.classList.remove('pdp__atc--soldout');
            if (atcBtn.dataset.origLabel) atcBtn.textContent = atcBtn.dataset.origLabel;
          }
        }

        // A disabled button with no way forward is a dead end, so the notify
        // trigger rides along with it — retargeted at the colour that was
        // actually picked, which is what tags the signup to that combo.
        var notifyBtn = root.querySelector('[data-fp-notify]');
        if (notifyBtn) {
          if (sw.dataset.fpAvailable === '0') {
            var href = sw.getAttribute('href') || '';
            notifyBtn.href = href;
            notifyBtn.setAttribute('data-notify-handle', href.split('/products/').pop());
            notifyBtn.setAttribute('data-notify-label',
              sw.getAttribute('title') || sw.dataset.fpColor || '');
            notifyBtn.hidden = false;
          } else {
            notifyBtn.hidden = true;
          }
        }

        // "View full details" follows the selected colour to its own page.
        var details = root.querySelector('[data-fp-details]');
        if (details) details.href = sw.getAttribute('href');

        // Colour label + selected tile.
        var label = root.querySelector('[data-fp-color-label]');
        if (label && sw.dataset.fpColor) label.textContent = sw.dataset.fpColor;
        var prefix = root.querySelector('[data-fp-color-prefix]');
        if (prefix && sw.dataset.fpColor) {
          prefix.textContent = sw.dataset.fpColor.indexOf('&') > -1 ? 'Combo:' : 'Color:';
        }
        swatches.forEach(function (s) {
          var on = s === sw;
          s.classList.toggle('is-current', on);
          if (on) s.setAttribute('aria-current', 'true');
          else s.removeAttribute('aria-current');
        });
      });
    });
  }

  function boot(scope) {
    init(scope);
    initBuybox(scope);
    initSwatches(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
